import { FC } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { useNewFrameShown } from "../../../next/shell/useNewFrameShown";
import { hasPermission } from "../../../utils/permissions";
import { useTranslation } from "../../../hooks/useTranslation";
import { ONBOARDING_ROOT } from "../../../backend/sagas/onboardingRedirect";

/**
 * Shown app-wide while an account holds more contacts than its plan covers
 * (D12). Mounted once near the root, beside TrialWidget (see App/index.tsx) —
 * same "single always-mounted widget reading billingStore" pattern.
 *
 * Two severities share this one banner:
 * - grace (warning): `contactLimitOver` but not yet `overContactLimit` — the
 *   account is over its plan but a grace window is still open.
 * - refused (error): `overContactLimit` — writes are refused right now.
 *
 * Never during registration: the onboarding flow has its own inline
 * messaging (ContactIntakePanels), same split TrialWidget/CountryNudge
 * already make for that flow.
 */
const OverLimitBanner: FC = () => {
  const { p } = useTranslation("contacts");
  const { pathname } = useLocation();
  const billing = useSelector((s: any) => s.billingStore ?? {});
  const user = useSelector((s: any) => s.authStore?.currentUser);

  // The new frame has its own strip for this (walkthrough decision 5).
  const newFrameShown = useNewFrameShown();

  if (newFrameShown || !billing.contactLimitOver || pathname.startsWith(ONBOARDING_ROOT)) return null;

  const canBill = hasPermission("read_billing", user);
  const values = { current: billing.contactCount, limit: billing.contactLimit };
  const grace = !billing.overContactLimit && billing.contactLimitGraceUntil;

  return (
    <Box
      sx={{
        position: "fixed",
        insetInline: 16,
        // A phone's Save button (VisitPage's report form, HouseholdDetailsPage)
        // sits flush with the bottom safe area — clear of it, rather than on
        // top of it, at narrow widths.
        bottom: { xs: 72, sm: 16 },
        zIndex: 1250,
        maxWidth: 720,
        marginInline: "auto",
      }}
    >
      <Alert
        severity={grace ? "warning" : "error"}
        action={canBill ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* /households, not /contacts — that's the actual route the
                sidebar's "Contacts" entry uses (constants/sideBarRoutes.js);
                /contacts 404s (verified in browser, Task 17 Step 5). */}
            <Button component={Link} to="/households" size="small" color="inherit">{p("over_limit_delete")}</Button>
            <Button component={Link} to="/pricing" size="small" variant="contained">{p("over_limit_upgrade")}</Button>
          </Box>
        ) : undefined}
      >
        {grace ? (
          p("over_limit_grace", { ...values, date: new Date(billing.contactLimitGraceUntil).toLocaleDateString() })
        ) : (
          <>
            <AlertTitle>{p("over_limit_title", values)}</AlertTitle>
            {canBill ? p("over_limit_body") : p("over_limit_body_team")}
          </>
        )}
      </Alert>
    </Box>
  );
};

export default OverLimitBanner;
